/* Copyright (c) 2023-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable, NgZone } from '@angular/core';
import { Subscription } from 'rxjs';
import { Logger, LoggingService } from 'crimson/lib/logging/logging.service';
import { WindowService } from 'crimson/lib/window/window.service';
import { InputModalityService } from 'input-modality/lib/input-modality.service';
import { NvInputModality } from 'input-modality/lib/input-modality.type';

interface ElementData {
    cn?: string; // Class Name
    it?: string; // Inner Text
    nn?: string; // Node Name
    sc?: ElementData; // Spatial Navigation Container
}

interface FocusEventData {
    ts: number; // TimeStamp: since Epoch in ms
    t: ElementData | null; // Target
    rt: ElementData | null; // Related Target
    i: NvInputModality; // Input Type: input modality of the focus event. Keyboard, mouse, gamepad, etc.,
}

interface FocusQueueItem {
    fo: FocusEventData | null; // Focus Out
    fi: FocusEventData | null; // Focus In
    fl: boolean; // Focus Lost
    ae?: ElementData; // Active Element: available if the focus_in.target is not document.activelement.
}

/**
 * A focus loss is defined to occur when the relatedTarget is null or is not connected to the DOM.
 * There are cases where the focus is set programmatically. To avoid logging focus loss in such instances set the attribute data-focus-loss-exempt on corresponding element.
 * To read the focus history queue:
 ** Look at the last focus out ("fo") event in the queue to understand the element that lost the focus.
 ** The corresponding focus in ("fi") event should signify the element that last had focus before the focus loss.
 * After a focus loss, during the next focus-in, the focus gain is logged to indicate the current element that has the focus.
 **/
@Injectable({
    providedIn: 'root'
})
export class FocusHistoryQueueService {
    private queue: FocusQueueItem[];
    private lastInputType: NvInputModality;
    private maxLength = 0;
    private readonly logger: Logger;
    private readonly window: Window;
    private inputModalitySubscription: Subscription;

    constructor(
        private ngZone: NgZone,
        loggingService: LoggingService,
        windowService: WindowService,
        private inputModalityService: InputModalityService
    ) {
        this.logger = loggingService.getLogger('FocusHistoryQueueService');
        this.window = windowService.window;
    }

    private enqueue(data: FocusQueueItem) {
        if (this.queue.push(data) > this.maxLength) {
            this.queue.shift();
        }
    }

    private buildElementData(element: HTMLElement, level: number = 0): ElementData {
        if (!element) {
            return null;
        }

        const elementData: ElementData = {
            nn: element.nodeName?.toUpperCase?.()
        };
        // In case of most element types, innertext alone is helpful to uniquely identify an element.
        // However, innertext is not used in two cases:
        // 1. When it's not available in case of button, a, mat-select, div, mat-dialog-container elements.
        // 2. In all other elements, due to PII concerns.
        if (elementData.nn === 'BUTTON') {
            // There exist a few cases where buttons don't have any inner text therefore using the icon name.
            // For instance, Hamburger icon (Menu) situated on the top-left in mall doesn't have any innertext.
            elementData.it =
                element.textContent ||
                element.querySelector('mat-icon[data-mat-icon-name]')?.getAttribute('data-mat-icon-name');
        } else if (!!element.textContent && (elementData.nn === 'A' || elementData.nn === 'MAT-SELECT')) {
            // The full inner text of the A and MAT-SELECT elements is required to locate the element while debugging.
            elementData.it = element.textContent;
        } else if (!!element.textContent && (elementData.nn === 'DIV' || elementData.nn === 'MAT-DIALOG-CONTAINER')) {
            // The full inner text of the DIV and MAT-DIALOG-CONTAINER elements is NOT required to identify the element.
            // So, limiting the text to 50 characters.
            elementData.it = element.textContent.substring(0, 50);
        }
        if (!elementData.it) {
            elementData.cn = element.className;
            if (level === 0) {
                elementData.sc = this.buildElementData(
                    element.getSpatialNavigationContainer() as HTMLElement,
                    level + 1
                );
            }
        }
        return elementData;
    }

    private buildFocusEventData(event: FocusEvent): FocusEventData {
        return {
            ts: event.timeStamp,
            t: this.buildElementData(event.target as HTMLElement),
            rt: this.buildElementData(event.relatedTarget as HTMLElement),
            i: this.lastInputType
        };
    }

    private focusIn = (event: FocusEvent) => {
        const previousEventLostFocus = this.queue.length > 0 && this.queue[this.queue.length - 1].fl;
        const queueItemData: FocusQueueItem = {
            fo: null,
            fi: this.buildFocusEventData(event),
            fl: false
        };
        if (event.target !== document.activeElement) {
            // Adding this as a cautionary measure. Not sure if this scenario would ever occur.
            queueItemData.ae = this.buildElementData(document.activeElement as HTMLElement);
        }
        this.enqueue(queueItemData);
        if (previousEventLostFocus) {
            this.logger.info('Focus regained. Focus on: ', JSON.stringify(queueItemData.fi));
        }
    };

    private focusOut = (event: FocusEvent) => {
        const focusOutDataItem = this.buildFocusEventData(event);
        let queueItemData: FocusQueueItem;
        // According to the MDN documentation, a focus-out event without a corresponding focus-in event is considered a focus loss.
        // However, in this case, the order is intentionally changed to capture the first focus-in event, specifically when the mall loads for the first time.
        // This means that initially, we will have a focus-in event without a previous focus-out event.
        // In instances where the focus out exists without a previous focus-in can be treated as a focus loss.
        if (this.queue.length === 0 || this.queue[this.queue.length - 1].fo !== null) {
            queueItemData = {
                fo: focusOutDataItem,
                fl: this.shouldCheckFocusLoss(event.target as HTMLElement),
                fi: null
            };
            this.enqueue(queueItemData);
        } else {
            queueItemData = this.queue[this.queue.length - 1];
            queueItemData.fo = focusOutDataItem;
            queueItemData.fl =
                this.shouldCheckFocusLoss(event.target as HTMLElement) &&
                (event.relatedTarget === null || !(event.relatedTarget as HTMLElement)?.isConnected);
        }
        if (queueItemData.fl) {
            this.logger.warn('Potential focus loss. Focus history: ', JSON.stringify(this.queue));
        }
    };

    private shouldCheckFocusLoss(element: HTMLElement) {
        // When the last action was via Touch/Mouse, the next focusable item is not auto-determined. Therefore, focus-in is not auto triggered.
        if (this.lastInputType === NvInputModality.Touch || this.lastInputType === NvInputModality.Mouse) {
            return false;
        }
        return !element?.hasAttribute('data-focus-loss-exempt');
    }

    start(size: number) {
        this.logger.info('Starting Focus History Queue Service');
        this.lastInputType = null;
        this.maxLength = size;
        this.queue = [];
        this.ngZone.runOutsideAngular(() => {
            this.window.addEventListener('focusin', this.focusIn);
            this.window.addEventListener('focusout', this.focusOut);
            this.inputModalitySubscription = this.inputModalityService.inputModalityChanged$.subscribe(
                (inputModality: NvInputModality) => {
                    this.lastInputType = inputModality;
                }
            );
        });
    }

    stop() {
        this.logger.info('Stopping Focus History Queue Service');
        this.ngZone.runOutsideAngular(() => {
            this.window.removeEventListener('focusin', this.focusIn);
            this.window.removeEventListener('focusout', this.focusOut);
            this.inputModalitySubscription?.unsubscribe();
        });
    }
}
